import { SITE } from '../config/site'
import { LegalLayout } from './LegalLayout'
import styles from './LegalPage.module.css'

export function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lede={`This policy explains how ${SITE.legalName} handles information in connection with ${SITE.productName}, this website, purchases, licensing, updates, and support.`}
    >
      <section>
        <h2>1. Who we are</h2>
        <p>
          {SITE.productName} is a product of {SITE.legalName}, a Wyoming limited
          liability company. In this policy, “Gesturely,” “we,” “us,” and “our” refer
          to {SITE.legalName}.
        </p>
      </section>

      <section>
        <h2>2. Plain-English actions are processed on your Mac</h2>
        <p>
          Gesturely resolves the Plain-English instructions you give to actions
          locally on your device. That interpretation does not require a cloud large
          language model, an external API key, or a Gesturely account.
        </p>
        <p>
          This does not mean Gesturely never uses a network connection. Website
          delivery, purchases, license fulfillment and restoration, support email,
          and software update checks may involve network services as described below.
        </p>
      </section>

      <section>
        <h2>3. Information involved in providing Gesturely</h2>
        <h3>Information you provide</h3>
        <p>
          We may receive information you choose to provide, such as the email address
          used for purchase and license delivery and the contents of messages you send
          to support.
        </p>

        <h3>Purchases</h3>
        <p>
          Payments are processed by Paddle, our Merchant of Record. Paddle collects
          and processes the payment and billing information needed to complete the
          transaction. We do not directly collect or store your full payment-card
          details. Paddle may provide us with order and customer information needed
          for product fulfillment, purchase support, fraud prevention, and records.
          Paddle handles that information under its own{' '}
          <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noreferrer">
            privacy policy
          </a>.
        </p>

        <h3>Licensing and activation</h3>
        <p>
          License fulfillment and restoration use the purchase and license information
          necessary to deliver a license, confirm entitlement, support activation on
          up to two personal Macs, prevent abuse, and respond to purchase-related
          support requests. We do not describe additional device identifiers or
          telemetry here because no additional fields have been confirmed for this
          policy.
        </p>

        <h3>Support communications</h3>
        <p>
          If you contact us, we receive your email address and the information in your
          message and attachments. We use it to answer the request, troubleshoot the
          issue, and maintain an appropriate support record.
        </p>

        <h3>Software updates</h3>
        <p>
          Gesturely uses the Sparkle framework to check for and deliver software
          updates. Update checks connect to update infrastructure and may create
          ordinary technical request records, such as an IP address and request
          metadata, at the infrastructure that serves the update feed and files.
        </p>

        <h3>Website use</h3>
        <p>
          The website must be delivered through hosting infrastructure, which may
          process ordinary connection and security information such as IP address,
          browser type, requested page, and request time in server logs. The current
          website does not include a marketing analytics package, advertising pixel,
          or cookie consent system.
        </p>
      </section>

      <section>
        <h2>4. How information is used</h2>
        <p>We use information only as reasonably needed to:</p>
        <ul>
          <li>complete purchases and deliver or restore licenses;</li>
          <li>operate, secure, maintain, and improve Gesturely and its website;</li>
          <li>provide customer support and communicate about a request;</li>
          <li>deliver software updates;</li>
          <li>prevent fraud, abuse, and unauthorized resale; and</li>
          <li>meet accounting, tax, legal, and regulatory obligations.</li>
        </ul>
      </section>

      <section>
        <h2>5. Service providers</h2>
        <p>
          We use service providers where they are needed to operate Gesturely. These
          include Paddle for checkout and payment processing, infrastructure for
          website and software-update delivery, systems used for license fulfillment
          and restoration, and email delivery used for licenses and support. These
          providers may process information on our behalf or, in Paddle’s case, as an
          independent party and Merchant of Record under its own terms and policies.
        </p>
        <p>
          The vendors used for hosting, email delivery, update delivery, and licensing
          infrastructure may change as those systems evolve. We use them only for the
          operational purposes described in this policy.
        </p>
      </section>

      <section>
        <h2>6. Retention and security</h2>
        <p>
          We keep information for as long as reasonably necessary for the purpose for
          which it was collected, including license and purchase fulfillment, support,
          security, fraud prevention, and legal or accounting obligations. Retention
          periods vary by the type of record and applicable requirements.
        </p>
        <p>
          We use reasonable administrative and technical safeguards appropriate to
          the information we handle. No method of storage or transmission is completely
          secure, so we cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>7. Your choices and rights</h2>
        <p>
          Depending on where you live, you may have rights to request access,
          correction, deletion, restriction, or a copy of certain personal information,
          or to object to certain processing. These rights may be subject to legal
          exceptions. To make a request about information handled by Gesturely, contact
          us using the details below. Requests concerning information controlled by
          Paddle should be directed to Paddle.
        </p>
      </section>

      <section>
        <h2>8. Changes to this policy</h2>
        <p>
          We may update this policy to reflect changes in Gesturely, our practices, or
          applicable law. The effective date above shows when this version took effect.
        </p>
      </section>

      <section>
        <h2>9. Contact</h2>
        <p>
          Privacy questions and requests can be sent to{' '}
          <a href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a>.
        </p>
        <address className={styles.address}>
          {SITE.legalName}
          <br />
          {SITE.legalAddress[0]}
          <br />
          {SITE.legalAddress[1]}
          <br />
          {SITE.legalAddress[2]}
        </address>
      </section>
    </LegalLayout>
  )
}
